import { isNotNil, isNotNilOrEmpty } from "client/Misc/utils";
import { NodeSpec } from "prosemirror-model";

export type SupportedDefaultFontMarks = {
	isBold: boolean;
	isItalic: boolean;
	isUnderline: boolean;
	isStrikeThrough: boolean;
	fontFamily: string;
	fontSize: string;
	color: string;
};

export const emptyDefaultFontMarks: Readonly<SupportedDefaultFontMarks> = {
	isBold: false,
	isItalic: false,
	isUnderline: false,
	isStrikeThrough: false,
	fontFamily: "",
	fontSize: "",
	color: ""
};

export type DefaultFontMarksNodeAttributesPart = {
	defaultFontMarks: SupportedDefaultFontMarks | undefined;
};

export type SetDefaultFontMarksNodeAttributesPart = NonNullableProperties<DefaultFontMarksNodeAttributesPart>;

export function parseDefaultFontMarks(htmlElement: HTMLElement): DefaultFontMarksNodeAttributesPart {
	const shouldReadStylesAsDefaultMarks: boolean = htmlElement.firstChild === null;

	if (!shouldReadStylesAsDefaultMarks) {
		return { defaultFontMarks: undefined };
	}

	const style: CSSStyleDeclaration = htmlElement.style;

	const isBold: boolean = style.fontWeight === "bold" || Number(style.fontWeight) >= 700;
	const isItalic: boolean = style.fontStyle === "italic";
	const allDecorations = new Set<string>(
		style.textDecoration
			.split(" ")
			.concat(
				...style.textDecorationLine
					.split(" ")
			)
			.map(x => x.trim())
	);

	const isUnderline: boolean = allDecorations.has("underline");
	const isStrikeThrough: boolean = allDecorations.has("line-through");

	const fontFamily: string = style.fontFamily;
	const fontSize: string = style.fontSize;
	const color: string = style.color;

	return {
		defaultFontMarks: {
			isBold,
			isItalic,
			isUnderline,
			isStrikeThrough,
			fontFamily,
			fontSize,
			color
		}
	};
}

export function generateDefaultFontMarkStyles(attrs: DefaultFontMarksNodeAttributesPart): Record<string, string> {
	const result: Record<string, string> = {};
	const defaultFontMarks: SupportedDefaultFontMarks | undefined = attrs.defaultFontMarks;

	if (!defaultFontMarks) {
		return result;
	}

	const {
		isBold,
		isItalic,
		isUnderline,
		isStrikeThrough,
		fontFamily,
		fontSize,
		color
	} = defaultFontMarks;

	if (isBold) {
		result["font-weight"] = "bold";
	}

	if (isItalic) {
		result["font-style"] = "italic";
	}

	let textDecoration: string | undefined = undefined;

	if (isUnderline) {
		textDecoration = "underline";
	}

	if (isStrikeThrough) {
		if (textDecoration === undefined) {
			textDecoration = "line-through";
		} else {
			textDecoration += " line-through";
		}
	}

	if (isNotNil(textDecoration)) {
		result["text-decoration"] = textDecoration;
	}

	if (isNotNilOrEmpty(fontFamily)) {
		result["font-family"] = fontFamily;
	}

	if (isNotNilOrEmpty(fontSize)) {
		result["font-size"] = fontSize;
	}

	if (isNotNilOrEmpty(color)) {
		result["color"] = color;
	}

	return result;
}

export type NodeSpecWithDefaultFontMarksSupport = {
	doesSupportDefaultFontMarks: true;
};

export function doesSupportDefaultFontMarks(nodeType: NodeSpec): nodeType is (NodeSpec & NodeSpecWithDefaultFontMarksSupport) {
	return (nodeType as NodeSpecWithDefaultFontMarksSupport).doesSupportDefaultFontMarks === true;
}

export function isDefaultFontMarksNodeAttributes(attrs: unknown): attrs is SetDefaultFontMarksNodeAttributesPart {
	if (typeof attrs !== "object" || attrs === null) {
		return false;
	}

	const defaultFontMarks: SupportedDefaultFontMarks | undefined = (attrs as DefaultFontMarksNodeAttributesPart)
		.defaultFontMarks;

	return typeof defaultFontMarks === "object" && defaultFontMarks !== null;
}